import { Controller } from "@hotwired/stimulus"

// De rondleiding: een donkere laag met een gat om het ding dat hij aanwijst,
// en een kaart met uitleg. De laag is data-turbo-permanent: bij een stap op
// een ander scherm vervangt Turbo alleen de pagina eronder, en de laag blijft
// staan. Tijdens het wisselen sluit het gat zich tot een volle verduistering,
// daarna opent het zich om het nieuwe doel. Zo ziet de gebruiker geen wit
// scherm en geen laag die verdwijnt en terugkomt.
export default class extends Controller {
  static targets = ["shade", "highlight", "card", "progress", "title", "body", "previous", "next"]
  static values = {
    steps: Array,
    completeUrl: String,
    autoStart: Boolean,
    storageKey: String,
    progressTemplate: String
  }

  connect() {
    // Turbo verplaatst een permanent element bij elk bezoek, en Stimulus meldt
    // dat als een nieuwe connect op dezelfde controller. Alleen de eerste keer
    // is het echt een nieuwe pagina.
    this.listen()
    if (this.started) return
    this.started = true
    this.steps = this.stepsValue
    this.animate()

    const stored = this.storedState()
    if (stored?.active) {
      // Alleen doorgaan op het scherm waar de rondleiding zelf naartoe stuurde.
      // Ga je zelf ergens anders heen, dan laat hij je gaan; anders word je bij
      // elke tik op de balk teruggetrokken naar zijn eigen stap. Gezien op
      // 23-09-2026 op productie.
      if (stored.pad && stored.pad !== window.location.pathname) {
        this.clearStoredState()
      } else {
        requestAnimationFrame(() => this.show(stored.index, stored.direction || 1))
      }
    } else if (this.autoStartValue) {
      requestAnimationFrame(() => this.show(0, 1))
    }
  }

  disconnect() {
    this.unlisten()
  }

  listen() {
    if (this.listening) return
    this.listening = true
    this.startListener = (event) => this.start(event.detail?.mode)
    this.layoutListener = () => this.layout()
    this.keyListener = (event) => this.keydown(event)
    this.pauseListener = () => this.pause()
    this.loadListener = () => this.arrived()
    window.addEventListener("rondleiding:start", this.startListener)
    window.addEventListener("rondleiding:pause", this.pauseListener)
    window.addEventListener("resize", this.layoutListener)
    window.addEventListener("scroll", this.layoutListener, true)
    window.addEventListener("keydown", this.keyListener)
    document.addEventListener("turbo:load", this.loadListener)
  }

  unlisten() {
    // Bij een verplaatsing door Turbo volgt de connect meteen. Pas als het
    // element na een tik echt weg is, horen de luisteraars weg.
    queueMicrotask(() => {
      if (this.element.isConnected || !this.listening) return
      this.listening = false
      window.removeEventListener("rondleiding:start", this.startListener)
      window.removeEventListener("rondleiding:pause", this.pauseListener)
      window.removeEventListener("resize", this.layoutListener)
      window.removeEventListener("scroll", this.layoutListener, true)
      window.removeEventListener("keydown", this.keyListener)
      document.removeEventListener("turbo:load", this.loadListener)
      this.unlockPage()
    })
  }

  start(mode) {
    const index = mode === "current" ? this.firstStepOnCurrentScreen() : 0
    this.show(index, 1)
  }

  previous() {
    this.show(this.index - 1, -1)
  }

  next() {
    if (this.index === this.steps.length - 1) {
      this.complete()
    } else {
      this.show(this.index + 1, 1)
    }
  }

  close() {
    this.complete()
  }

  keydown(event) {
    if (this.element.hidden) return

    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (event.key === "Tab") {
      this.keepFocusInside(event)
    }
  }

  show(index, direction) {
    if (index < 0) index = 0
    if (index >= this.steps.length) return this.complete()

    const step = this.steps[index]
    this.store({ active: true, index, direction, pad: step.path })

    if (step.path !== window.location.pathname) {
      this.travel(index, direction, step.path)
      return
    }

    const target = this.visibleTarget(step.selectors)
    if (!target) {
      this.show(index + direction, direction)
      return
    }

    this.pending = null
    this.index = index
    this.targetElement = target
    this.element.hidden = false
    this.element.removeAttribute("aria-busy")
    document.body.style.overflow = "hidden"
    this.progressTarget.textContent = this.progressTemplateValue
      .replace("__CURRENT__", index + 1)
      .replace("__TOTAL__", this.steps.length)
    this.titleTarget.textContent = step.title
    this.bodyTarget.textContent = step.body
    this.fadeText(1)
    this.previousTarget.disabled = index === 0
    this.previousTarget.classList.toggle("invisible", index === 0)
    this.nextTarget.disabled = false
    this.nextTarget.textContent = index === this.steps.length - 1
      ? this.nextTarget.dataset.finishLabel
      : this.nextTarget.dataset.nextLabel

    target.scrollIntoView({ behavior: "auto", block: "center", inline: "nearest" })
    requestAnimationFrame(() => {
      this.highlightTarget.style.opacity = "1"
      this.layout()
      this.keepTargetAboveMobileCard()
      this.cardTarget.focus({ preventScroll: true })
    })
  }

  // Naar het scherm van de volgende stap, zonder de laag los te laten: het gat
  // sluit, de tekst dimt, en Turbo haalt het scherm op.
  travel(index, direction, path) {
    this.pending = { index, direction, path }
    this.targetElement = null
    this.element.hidden = false
    this.element.setAttribute("aria-busy", "true")
    this.nextTarget.disabled = true
    this.previousTarget.disabled = true
    this.highlightTarget.style.opacity = "0"
    this.fadeText(0.35)
    this.layoutShades(null)
    // Onder de verduistering terug naar de gewone plek, anders springt de
    // kaart pas zodra het nieuwe scherm er staat.
    if (!window.matchMedia("(min-width: 1024px)").matches) {
      this.cardTarget.style.removeProperty("top")
      this.cardTarget.style.removeProperty("bottom")
    }

    if (window.Turbo?.visit) {
      window.Turbo.visit(path)
    } else {
      window.location.assign(path)
    }
  }

  // Na elk Turbo-bezoek. Stuurde de rondleiding je hierheen, dan de stap
  // tonen. Een doorverwijzing (Facturen zonder koppeling gaat naar de
  // koppelroute) landt op een ander pad: dan zoekt hij het doel van die stap
  // hier, en anders gaat hij door met de volgende. Tikte je zelf ergens op,
  // dan laat hij je gaan.
  arrived() {
    const pending = this.pending
    if (pending) {
      const step = this.steps[pending.index]
      if (window.location.pathname !== pending.path) {
        this.steps.splice(pending.index, 1, { ...step, path: window.location.pathname })
      }
      requestAnimationFrame(() => this.show(pending.index, pending.direction))
    } else if (new URLSearchParams(window.location.search).get("rondleiding") === "1") {
      // "Rondleiding opnieuw" komt via Turbo hier: de laag die bleef staan
      // kent die vraag niet uit zichzelf.
      requestAnimationFrame(() => this.show(0, 1))
    } else if (!this.element.hidden) {
      this.pause()
    }
  }

  layout() {
    if (this.element.hidden || !this.targetElement?.isConnected) return

    const rect = this.paddedRect(this.targetElement.getBoundingClientRect())
    this.layoutShades(rect)
    Object.assign(this.highlightTarget.style, {
      left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px`
    })

    if (window.matchMedia("(min-width: 1024px)").matches) this.placeDesktopCard(rect)
  }

  // Zonder rechthoek dekt de bovenste strook het hele scherm: volle
  // verduistering terwijl het volgende scherm laadt.
  layoutShades(rect) {
    const width = window.innerWidth
    const height = window.innerHeight
    const hole = rect || { left: width / 2, top: height / 2, right: width / 2, bottom: height / 2, width: 0, height: 0 }
    const boxes = rect
      ? [
          { left: 0, top: 0, width, height: hole.top },
          { left: 0, top: hole.top, width: hole.left, height: hole.height },
          { left: hole.right, top: hole.top, width: width - hole.right, height: hole.height },
          { left: 0, top: hole.bottom, width, height: height - hole.bottom }
        ]
      : [
          { left: 0, top: 0, width, height },
          { left: 0, top: 0, width: 0, height: 0 },
          { left: 0, top: 0, width: 0, height: 0 },
          { left: 0, top: 0, width: 0, height: 0 }
        ]
    this.shadeTargets.forEach((shade, index) => {
      const box = boxes[index]
      Object.assign(shade.style, {
        left: `${box.left}px`, top: `${box.top}px`, width: `${Math.max(0, box.width)}px`, height: `${Math.max(0, box.height)}px`
      })
    })
  }

  placeDesktopCard(target) {
    const gap = 16
    const margin = 12
    const width = this.cardTarget.offsetWidth
    const height = this.cardTarget.offsetHeight
    const clampX = (value) => Math.max(margin, Math.min(value, window.innerWidth - width - margin))
    const clampY = (value) => Math.max(margin, Math.min(value, window.innerHeight - height - margin))
    const candidates = [
      { x: target.right + gap, y: clampY(target.top) },
      { x: target.left - width - gap, y: clampY(target.top) },
      { x: clampX(target.left), y: target.bottom + gap },
      { x: clampX(target.left), y: target.top - height - gap }
    ]
    const position = candidates.find(({ x, y }) => (
      x >= margin && y >= margin && x + width <= window.innerWidth - margin && y + height <= window.innerHeight - margin &&
      !this.overlaps({ left: x, top: y, right: x + width, bottom: y + height }, target)
    )) || { x: margin, y: margin }

    Object.assign(this.cardTarget.style, { left: `${position.x}px`, top: `${position.y}px`, right: "auto", bottom: "auto" })
  }

  keepTargetAboveMobileCard() {
    if (window.matchMedia("(min-width: 1024px)").matches || !this.targetElement) return

    // Eerst terug naar onderaan, want daar hoort de kaart op de telefoon.
    this.cardTarget.style.removeProperty("top")
    this.cardTarget.style.removeProperty("bottom")
    const safeBottom = this.cardTarget.getBoundingClientRect().top - 12

    // Een tab in de onderbalk staat altijd onder de kaart. En op een kort
    // scherm valt er niets te schuiven: dan bleef de kaart over de knop Boeken
    // staan. In beide gevallen gaat de kaart naar boven.
    let onTop = Boolean(this.targetElement.closest(".onderbalk"))
    if (!onTop && this.targetElement.getBoundingClientRect().bottom > safeBottom) {
      window.scrollBy({ top: this.targetElement.getBoundingClientRect().bottom - safeBottom, behavior: "auto" })
      onTop = this.targetElement.getBoundingClientRect().bottom > safeBottom
    }
    if (onTop) {
      this.cardTarget.style.top = "0.75rem"
      this.cardTarget.style.bottom = "auto"
    }
    requestAnimationFrame(() => this.layout())
  }

  paddedRect(rect) {
    const padding = 6
    const left = Math.max(0, rect.left - padding)
    const top = Math.max(0, rect.top - padding)
    const right = Math.min(window.innerWidth, rect.right + padding)
    const bottom = Math.min(window.innerHeight, rect.bottom + padding)
    return { left, top, right, bottom, width: right - left, height: bottom - top }
  }

  visibleTarget(selectors) {
    for (const selector of selectors || []) {
      const found = [...document.querySelectorAll(selector)].find((element) => {
        if (this.element.contains(element)) return false
        const style = getComputedStyle(element)
        const rect = element.getBoundingClientRect()
        return style.display !== "none" && style.visibility !== "hidden" && rect.width > 0 && rect.height > 0
      })
      if (found) return found
    }
    return null
  }

  firstStepOnCurrentScreen() {
    const index = this.steps.findIndex((step) => step.path === window.location.pathname && this.visibleTarget(step.selectors))
    return index >= 0 ? index : 0
  }

  overlaps(first, second) {
    return first.left < second.right && first.right > second.left && first.top < second.bottom && first.bottom > second.top
  }

  reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  // Het gat glijdt van het ene doel naar het volgende in plaats van te
  // springen. Wie minder beweging wil, krijgt de sprong.
  animate() {
    if (this.reducedMotion()) return

    const easing = "cubic-bezier(0.2, 0, 0, 1)"
    const moving = ["left", "top", "width", "height"].map((property) => `${property} 260ms ${easing}`).join(", ")
    this.shadeTargets.forEach((shade) => { shade.style.transition = moving })
    this.highlightTarget.style.transition = `${moving}, opacity 160ms ease-out`
    ;[this.progressTarget, this.titleTarget, this.bodyTarget].forEach((text) => { text.style.transition = "opacity 160ms ease-out" })
  }

  fadeText(opacity) {
    ;[this.progressTarget, this.titleTarget, this.bodyTarget].forEach((text) => { text.style.opacity = String(opacity) })
  }

  keepFocusInside(event) {
    const buttons = [...this.cardTarget.querySelectorAll("button:not([disabled])")]
      .filter((button) => button.offsetParent !== null)
    if (buttons.length === 0) return

    const first = buttons[0]
    const last = buttons[buttons.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  // Het hulppaneel opent over de rondleiding heen, en twee lagen over elkaar
  // leest als een storing. Pauzeren haalt de aanwijzer weg zonder de
  // rondleiding als gezien te noteren: vanuit het paneel start je hem opnieuw.
  pause() {
    if (this.element.hidden) return

    this.hide()
    this.clearStoredState()
  }

  complete() {
    this.hide()
    this.clearStoredState()
    fetch(this.completeUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "" },
      credentials: "same-origin",
      keepalive: true
    })
  }

  hide() {
    this.element.hidden = true
    this.targetElement = null
    this.pending = null
    this.unlockPage()
  }

  storedState() {
    try { return JSON.parse(sessionStorage.getItem(this.storageKeyValue)) } catch (_error) { return null }
  }

  store(state) {
    try { sessionStorage.setItem(this.storageKeyValue, JSON.stringify(state)) } catch (_error) { /* navigation still works */ }
  }

  clearStoredState() {
    try { sessionStorage.removeItem(this.storageKeyValue) } catch (_error) { /* nothing to clear */ }
  }

  unlockPage() {
    document.body.style.removeProperty("overflow")
  }
}
